// Forgot Password — sends a reset link via Firebase Auth.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../firebase";
import BrandPanel from "../components/auth/BrandPanel";
import Wordmark from "../components/auth/Wordmark";
import { useIsMobile } from "../lib/useMediaQuery";
import "../styles/auth.css";

const AIc = {
  alert: <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3" /><path d="M7 4.5V7.5M7 9.5V9.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>,
  check: <svg width="18" height="18" viewBox="0 0 14 14" fill="none"><path d="M3 7.5L6 10L11 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>,
};

export default function ForgotPassword() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email);
      setSent(true);
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (code === "auth/invalid-email") {
        setError("That doesn't look like a valid email.");
      } else if (code === "auth/user-not-found") {
        // Don't leak account existence — show the same "sent" UI.
        setSent(true);
      } else {
        setError("Couldn't send the email. Check your connection and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`auth ${isMobile ? "is-mobile" : "is-desktop"}`}>
      <div className="auth-frame">
        <BrandPanel variant="login" />
        <div className="auth-form-panel">
          <div className="auth-form-top">
            {isMobile && <Wordmark mobile />}
            <span className="auth-form-top-link">
              Remembered it? <Link to="/login">Log in</Link>
            </span>
          </div>
          <div className="auth-form-body">
            {sent ? (
              <div>
                <div className="auth-heading">
                  <h1>Check your email.</h1>
                  <p>
                    If an account exists for <strong>{email}</strong>, a reset link is on its way.
                    The link expires in an hour.
                  </p>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: "var(--status-hydrated-soft)",
                    color: "#0F5C30",
                    padding: "12px 14px",
                    borderRadius: "var(--r-md)",
                    fontSize: 13,
                    marginBottom: 18,
                  }}
                >
                  {AIc.check}
                  <span>Email sent. Check your inbox and spam folder.</span>
                </div>
                <button
                  type="button"
                  className="auth-submit"
                  onClick={() => navigate("/login")}
                >
                  Back to login →
                </button>
              </div>
            ) : (
              <form onSubmit={submit}>
                <div className="auth-heading">
                  <h1>Forgot your password?</h1>
                  <p>Enter your email and we'll send you a reset link.</p>
                </div>

                <div className="auth-fields">
                  <div className="auth-field">
                    <label className="auth-field-label" htmlFor="auth-email">Email</label>
                    <input
                      id="auth-email"
                      type="email"
                      className={`auth-input ${error ? "has-error" : ""}`}
                      placeholder="you@yardname.co.uk"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                    />
                    {error && <span className="auth-error">{AIc.alert} {error}</span>}
                  </div>
                </div>

                <button
                  className={`auth-submit ${loading ? "is-loading" : ""}`}
                  type="submit"
                  disabled={loading}
                >
                  {loading ? <><span className="auth-spinner"></span> Sending…</> : <>Send reset link →</>}
                </button>

                <p className="auth-foot">
                  Don't have an account? <Link to="/signup">Create a yard</Link>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
